<script setup>
import dayjs from 'dayjs'
import { computed } from 'vue'
import { getI18n, translateOrDefault } from '@/plugins/i18n'

import { chatActionStatusEnum, useChatStore } from '@/stores/chat.store'
import { formatCurrencyValue, useCurrency } from '@/composables/currency'
import { signalToneColor, signalToneEnum } from '@/composables/dashboard-kpi'
import ChatActionButtons from '@/components/chat/ChatActionButtons.vue'

const DEFAULT_ACTION_ICON = 'mdi-lightning-bolt-outline'
const PERIOD_PARAMS = Object.freeze(['start', 'end'])

const actionIcons = Object.freeze({
  assign_service: 'mdi-account-plus-outline',
  confirm_allocation: 'mdi-check-all',
  change_range: 'mdi-calendar-arrow-right',
  update_prices: 'mdi-tag-arrow-down-outline',
  clone: 'mdi-content-copy',
  toggle_archive: 'mdi-archive-outline',
  delete: 'mdi-delete-outline',
  add_condition: 'mdi-wrench-outline',
  record_expense: 'mdi-receipt-text-outline',
  prepare_expense_claim: 'mdi-file-document-edit-outline',
  approve_expense_claims: 'mdi-check-decagram-outline',
  reject_expense_claims: 'mdi-undo-variant',
  set_completed: 'mdi-checkbox-marked-circle-outline',
  create_list: 'mdi-playlist-plus',
  add_items: 'mdi-playlist-edit',
})

const actionStatusConfig = Object.freeze({
  [chatActionStatusEnum.CONFIRMED]: { color: 'positive', icon: 'mdi-check', labelKey: 'chat.actionStatus.confirmed' },
  [chatActionStatusEnum.CANCELLED]: { color: 'grey-6', icon: 'mdi-close', labelKey: 'chat.actionStatus.cancelled' },
  [chatActionStatusEnum.FAILED]: { color: 'negative', icon: 'mdi-alert-circle-outline', labelKey: 'chat.actionStatus.failed' },
})

const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
  action: {
    type: Object,
    required: true,
  },
})

const i18n = getI18n()
const chatStore = useChatStore()

const paramLabel = (name) => translateOrDefault(`permissions.param.${name}`)
const formatDateTime = (value) => dayjs(value).format('L LT')
const formatAmount = (value) => Number(value).toLocaleString(i18n.locale.value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const formatParam = (value) => {
  if (typeof value === 'boolean') return i18n.t(value ? 'common.yes' : 'common.no')
  return Array.isArray(value) ? value.join(', ') : value
}
const { getCurrencyString } = useCurrency()
const formatMoney = (value) => (preview.value.currency ? getCurrencyString(Number(value), preview.value.currency) : formatCurrencyValue(value))

const icon = computed(() => actionIcons[props.action.action?.split('.').at(-1)] ?? DEFAULT_ACTION_ICON)
const statusConfig = computed(() => actionStatusConfig[props.action.status])
const isPending = computed(() => props.action.status === chatActionStatusEnum.PENDING)
const isGrouped = computed(() => isPending.value && chatStore.pendingActions(props.message).length > 1)
const preview = computed(() => props.action.preview ?? {})
const references = computed(() => Object.entries(props.action.references ?? {}).map(([name, reference]) => ({ name, label: paramLabel(name), value: reference.name })))
const params = computed(() => Object.entries(props.action.params ?? {})
  .filter(([name, value]) => value !== null && !(name in (props.action.references ?? {})) && !PERIOD_PARAMS.includes(name))
  .map(([name, value]) => ({ name, label: paramLabel(name), value: preview.value.labels?.[name] ?? formatParam(value) })))
const period = computed(() => props.action.params?.start && `${formatDateTime(props.action.params.start)} – ${formatDateTime(props.action.params.end)}`)
const currentPeriod = computed(() => preview.value.current_start && `${formatDateTime(preview.value.current_start)} – ${formatDateTime(preview.value.current_end)}`)
const isUnavailable = computed(() => preview.value.availability && !preview.value.availability.available)
const priceChanges = computed(() => preview.value.changes ?? [])
const removedAllocations = computed(() => preview.value.removed_allocations ?? [])
const previewRows = computed(() => preview.value.rows ?? [])
const attentionClass = `text-${signalToneColor(signalToneEnum.ATTENTION)}`
</script>

<template>
  <q-card flat bordered class="q-mt-sm">
    <q-item dense class="q-pa-sm">
      <q-item-section avatar>
        <q-icon :name="icon" color="primary" />
      </q-item-section>
      <q-item-section>
        <q-item-label class="text-weight-medium">{{ translateOrDefault(action.label_key) }}</q-item-label>
        <q-item-label v-for="item in [...references, ...params]" :key="item.name" caption>
          {{ item.label }}: <span class="text-weight-medium">{{ item.value }}</span>
        </q-item-label>
        <q-item-label v-for="change in priceChanges" :key="change.id" caption>
          {{ change.name }}: {{ formatAmount(change.old) }} → <span class="text-weight-medium">{{ formatAmount(change.new) }}</span>
        </q-item-label>
        <q-item-label v-if="priceChanges.length && priceChanges.length < preview.count" caption>
          {{ i18n.t('chat.action.moreItems', { count: preview.count - priceChanges.length }) }}
        </q-item-label>
        <q-item-label v-for="row in previewRows" :key="row.label" caption>
          {{ row.label }}<template v-if="row.amount !== null">: <span class="text-weight-medium">{{ formatMoney(row.amount) }}</span></template>
        </q-item-label>
        <q-item-label v-if="preview.count" caption>{{ i18n.t('chat.action.expenseCount', { count: preview.count }) }}</q-item-label>
        <q-item-label v-if="preview.amount" caption class="text-weight-medium">{{ i18n.t('chat.action.total', { amount: formatMoney(preview.amount) }) }}</q-item-label>
        <q-item-label v-if="currentPeriod" caption>{{ currentPeriod }} → {{ period }}</q-item-label>
        <q-item-label v-else-if="period" caption>{{ period }}</q-item-label>
        <q-item-label v-if="isUnavailable" caption :class="attentionClass">{{ i18n.t('chat.action.unavailable') }}</q-item-label>
        <q-item-label v-if="removedAllocations.length" caption :class="attentionClass">
          {{ i18n.t('chat.action.removedAllocations', { projects: removedAllocations.join(', ') }) }}
        </q-item-label>
        <q-item-label v-if="action.status === chatActionStatusEnum.FAILED && action.detail" caption class="text-negative">{{ translateOrDefault(action.detail) }}</q-item-label>
      </q-item-section>
      <q-item-section v-if="isGrouped" side top>
        <q-checkbox
          :model-value="!action.excluded"
          :disable="chatStore.isStreaming || action.decision !== undefined"
          @update:model-value="chatStore.toggleActionIncluded(action)"
        >
          <q-tooltip>{{ i18n.t('chat.action.include') }}</q-tooltip>
        </q-checkbox>
      </q-item-section>
    </q-item>
    <chat-action-buttons v-if="isPending && !isGrouped" :message="message" />
    <q-card-actions v-else-if="!isPending" align="right">
      <q-chip
        dense
        :icon="statusConfig.icon"
        :color="statusConfig.color"
        text-color="white"
      >
        {{ i18n.t(statusConfig.labelKey) }}
      </q-chip>
    </q-card-actions>
  </q-card>
</template>
